// Pure helpers for the booking form's Workflow panel: which definition a booking
// uses, who is on which step, and what is sent to the server.

export const SKILL_PROFESSIONAL = "PROFESSIONAL";
const STARTED = ["IN_PROGRESS", "DONE", "SKIPPED"];

// The service a booking is for, the same way the server reads it: the first real
// offering line (not a custom charge, a part or a total).
export const serviceFromPriceSection = (priceSection) => {
  const line = (priceSection || []).find((i) => i && !i.isCustom && !i.isPart && Number(i.service_offering_id) > 0);
  return line ? { serviceId: Number(line.service_id) || null, offeringIds: [Number(line.service_offering_id)] } : null;
};

// The definition the panel shows. A booking keeps the steps it was made with - unless
// its service now resolves to a different workflow and nobody has started (the server
// then switches it on save, so the panel shows what will be saved).
// -> { template_id, name, steps[] } | null
export const pickDefinition = (resolved, existing) => {
  const fromTemplate = (t) => ({ template_id: t.id, name: t.name, steps: t.steps || [] });
  const fromExisting = (e) => ({ template_id: e.template_id, name: e.name, steps: e.steps || [] });
  if (existing && resolved) {
    const switched = Number(resolved.id) !== Number(existing.template_id);
    const started = (existing.steps || []).some((s) => STARTED.includes(s.state));
    return switched && !started ? fromTemplate(resolved) : fromExisting(existing);
  }
  if (existing) return fromExisting(existing);
  if (resolved) return fromTemplate(resolved);
  return null;
};

// { step_key: userId } for the steps that already have someone (from a saved booking).
export const assignmentsFromExisting = (existing, definition) => {
  const keys = new Set((definition?.steps || []).map((s) => s.step_key));
  const out = {};
  (existing?.steps || []).forEach((s) => {
    if (s.assignee_user_id && keys.has(s.step_key)) out[s.step_key] = Number(s.assignee_user_id);
  });
  return out;
};

// The step the booking's main picker (the crew calendar) stands for: the service provider
// step - the one flagged is_primary, else (a workflow made before the flag) the first step
// needing the professional skill. It is shown read-only and never sent - the server fills
// it from the picker, so the two can never disagree.
export const mirroredStepKey = (steps) => {
  const crew = (steps || []).filter((s) => s.kind === "CREW");
  return (crew.find((s) => s.is_primary) || crew.find((s) => s.skill_key === SKILL_PROFESSIONAL))?.step_key || null;
};

// True when the booking's service step is OPTIONAL: the vendor can book a time now and assign the
// service provider later. False for a Required service step (today's flow), and for a workflow
// without one. Works on a template's steps and on a booking's own steps alike.
export const isServiceStepOptional = (steps) => {
  const key = mirroredStepKey(steps);
  const step = key ? (steps || []).find((s) => s.step_key === key) : null;
  return !!step && !step.is_mandatory;
};

// What goes in the booking payload: every crew step except the mirrored one, with
// null for an empty step so a person who was taken off is really removed.
export const buildWorkflowPayload = (definition, assignments) => {
  if (!definition) return undefined;
  const mirrored = mirroredStepKey(definition.steps);
  return {
    template_id: definition.template_id,
    steps: definition.steps
      .filter((s) => s.kind === "CREW" && s.step_key !== mirrored)
      .map((s) => ({ step_key: s.step_key, user_id: assignments?.[s.step_key] || null })),
  };
};

// A comparable string for "did the assignments change" (the edit form's no-changes check).
export const assignmentsSignature = (definition, assignments) =>
  definition
    ? definition.steps
        .filter((s) => s.kind === "CREW" && s.step_key !== mirroredStepKey(definition.steps))
        .map((s) => `${s.step_key}:${assignments?.[s.step_key] || ""}`)
        .join(",")
    : "";

// Steps whose person can be chosen on the form, split so optional ones can be tucked away.
export const splitSteps = (definition) => {
  const steps = definition?.steps || [];
  return { required: steps.filter((s) => s.is_mandatory), optional: steps.filter((s) => !s.is_mandatory) };
};

// The skill filter to ask the server for: a step needing nothing in particular asks for everyone.
export const skillFor = (step) => step.skill_key || "ANY";

// Who is on which step, for a list or a detail view: "Ali (Service), Raj (Pickup)".
//   Uses the booking's `step_assignments` (the API adds it to booking rows that have a workflow).
//   Returns null when there is nothing worth adding to the plain crew names - no workflow, or
//   only the service provider is on it - so the caller keeps showing `provider_names` as before.
export const formatStepAssignments = (item) => {
  const steps = (item?.step_assignments || []).filter((s) => s.people?.length);
  if (steps.length < 2) return null;
  return steps
    .map((s) => `${s.people.map((p) => p.name || `#${p.user_id}`).join(" & ")} (${s.step_name})`)
    .join(", ");
};
